import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, CalendarClock, Newspaper } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { marcarVistos } from './feedApi';
import { quando, resumoDoPost } from './feedLogica';
import { useFeedNaoLidos } from './useFeedNaoLidos';
import { useAvisosAssembleia } from './avisoAssembleia';
import { useAvisosUsuario } from './useAvisosUsuario';
import { nomeDoFeed, useOrganizacaoAtiva } from '../organizacoes/useOrganizacaoAtiva';

/**
 * O sino de notificações (pedido do Rick, 26/09/2026): mostra o que chegou de
 * novo e leva direto para lá. Hoje a única fonte é o Feed North; o formato da
 * lista (ícone, título, resumo, destino) já serve para outras fontes depois.
 * Desde 30/09 também avisa assembleia chegando com cota sem lance
 * (avisoAssembleia.ts) — o "visto" desse aviso fica neste aparelho. Desde
 * 08/10, os avisos de cada pessoa (useAvisosUsuario.ts, migration 240): celular
 * repetido, transferência de contato — esses ficam lidos no banco.
 *
 * `tom`: "escuro" no menu lateral e no Início geral (fundo azul), "claro" no
 * cabeçalho do celular.
 */
export const SinoNotificacoes = ({ tom = 'escuro', className }: { tom?: 'escuro' | 'claro'; className?: string }) => {
  const { posts, total: totalFeed, somenteLeitura } = useFeedNaoLidos();
  const { naoVistos: avisos, marcarVistos: marcarAvisos } = useAvisosAssembleia({ ativo: !somenteLeitura });
  const { avisos: meus, marcarLidos } = useAvisosUsuario({ ativo: !somenteLeitura });
  const total = totalFeed + avisos.length + meus.length;
  const [aberto, setAberto] = useState(false);
  const { data: organizacao } = useOrganizacaoAtiva();
  const navigate = useNavigate();

  if (somenteLeitura) return null;

  const ir = (destino: string) => {
    setAberto(false);
    navigate(destino);
  };

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={total ? `Notificações: ${total} ${total === 1 ? 'nova' : 'novas'}` : 'Notificações'}
          className={cn(
            'relative inline-grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg transition-colors',
            tom === 'escuro' ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-foreground/70 hover:bg-muted',
            className,
          )}
        >
          <Bell className="h-[18px] w-[18px]" />
          {total > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
              {total > 9 ? '9+' : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <p className="text-sm font-semibold">Notificações</p>
          {total > 0 && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                if (posts.length) marcarVistos(posts.map((p) => p.id));
                marcarAvisos(avisos.map((a) => a.id));
                void marcarLidos(meus.map((a) => a.id));
              }}
            >
              Marcar como lidas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {meus.map((a) => (
            <button
              key={`meu-${a.id}`}
              type="button"
              onClick={() => {
                void marcarLidos([a.id]);
                if (a.link) ir(a.link);
              }}
              className="flex w-full gap-3 border-b px-3 py-2.5 text-left last:border-0 hover:bg-muted/60"
            >
              <span className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-sky-100 text-sky-700">
                <BellRing className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold">{a.titulo}</span>
                <span className="block whitespace-pre-line text-[12.5px] leading-snug text-foreground/80">{a.texto}</span>
                <span className="block text-[11.5px] text-muted-foreground">{quando(a.criado_em)}</span>
              </span>
              <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
            </button>
          ))}
          {avisos.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                marcarAvisos([a.id]);
                ir(`/lances?dia=${a.data}`);
              }}
              className="flex w-full gap-3 border-b px-3 py-2.5 text-left last:border-0 hover:bg-muted/60"
            >
              <span className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-amber-100 text-amber-700">
                <CalendarClock className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold">{a.titulo}</span>
                <span className="block text-[12.5px] leading-snug text-foreground/80">{a.texto}</span>
              </span>
              <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
            </button>
          ))}
          {posts.length === 0 && avisos.length === 0 && meus.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma notificação nova.</p>
          ) : (
            posts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => ir(`/feed?post=${p.id}`)}
                className="flex w-full gap-3 border-b px-3 py-2.5 text-left last:border-0 hover:bg-muted/60"
              >
                <span className="mt-0.5 grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <Newspaper className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold">Novo no {nomeDoFeed(organizacao)}</span>
                  <span className="block truncate text-[13px] text-foreground/80">{resumoDoPost(p)}</span>
                  <span className="block text-[11.5px] text-muted-foreground">
                    {p.autor_nome} · {quando(p.created_at)}
                  </span>
                </span>
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
              </button>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => ir('/feed')}
          className="w-full border-t px-3 py-2 text-center text-xs font-semibold text-primary hover:bg-muted/60"
        >
          Abrir o feed →
        </button>
      </PopoverContent>
    </Popover>
  );
};
